import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaCheckCircle,
  FaUser,
  FaComment,
  FaRegEnvelope,
  FaHeadset,
  FaClock,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/contact",
        formData,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );

      if (response.data.success) {
        setSubmitted(true);
        toast.success(
          "Message sent successfully! We will get back to you soon.",
        );
        setFormData({ name: "", email: "", subject: "", message: "" });
      }
    } catch (error) {
      console.error("Contact error:", error);
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "Email",
      details: ["vtugenx@gmail.com"],
      color: "#8b5cf6",
    },
    {
      icon: <FaPhone />,
      title: "Phone",
      details: [], //enter contact details in this
      color: "#10b981",
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Address",
      details: ["Bengaluru, Karnataka, India"],
      color: "#3b82f6",
    },
  ];

  if (submitted) {
    return (
      <div
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: "4rem 2rem",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          minHeight: "calc(100vh - 200px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "28px",
            padding: "3rem",
            textAlign: "center",
            border: `1px solid ${getBorderColor()}`,
            boxShadow: isDarkMode
              ? "0 25px 50px -12px rgba(0,0,0,0.5)"
              : "0 25px 50px -12px rgba(0,0,0,0.25)",
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            style={{
              width: "80px",
              height: "80px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <FaCheckCircle size={45} color="white" />
          </motion.div>
          <h2
            style={{
              marginTop: "1.5rem",
              fontSize: "1.8rem",
              fontWeight: "bold",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Message Sent!
          </h2>
          <p style={{ color: getTextSecondary(), marginTop: "0.5rem" }}>
            Thank you for reaching out. Our team will get back to you shortly.
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSubmitted(false)}
            style={{
              marginTop: "2rem",
              padding: "0.8rem 2rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "600",
              boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
            }}
          >
            Send Another Message
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            textDecoration: "none",
            fontSize: "0.9rem",
            fontWeight: 500,
            padding: "0.5rem 1rem",
            borderRadius: "10px",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            marginBottom: "1.5rem",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.2)"
              : "rgba(139,92,246,0.1)";
            e.target.style.transform = "translateX(-5px)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)";
            e.target.style.transform = "translateX(0)";
          }}
        >
          ← Back to Home
        </Link>

        {/* Header Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "70px",
                height: "70px",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
              }}
            >
              <FaHeadset size={32} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "2.5rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Get in <span style={{ color: "#8b5cf6" }}>Touch</span>
          </h1>
          <p
            style={{
              color: getTextSecondary(),
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Have questions? We'd love to hear from you. Send us a message and
            we'll respond as soon as possible.
          </p>
        </motion.div>

        {/* Main Content - Responsive Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? "1.5rem" : "3rem",
          }}
        >
          {/* Contact Info */}
          <motion.div
            variants={itemVariants}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              padding: isMobile ? "1.5rem" : "2rem",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.3rem" : "1.5rem",
                fontWeight: "bold",
                marginBottom: "1.5rem",
                color: getTextColor(),
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaRegEnvelope color="#8b5cf6" /> Contact Information
            </h2>

            <div style={{ marginBottom: "2rem" }}>
              {contactInfo.map((info, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                    padding: "1rem",
                    borderRadius: "16px",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "rgba(139,92,246,0.03)",
                    transition: "all 0.3s",
                  }}
                  whileHover={{ x: 5 }}
                >
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      background: `${info.color}15`,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: info.color,
                      fontSize: "1.5rem",
                    }}
                  >
                    {info.icon}
                  </div>
                  <div>
                    <h4
                      style={{
                        color: getTextColor(),
                        marginBottom: "0.3rem",
                        fontWeight: "600",
                      }}
                    >
                      {info.title}
                    </h4>
                    {info.details.map((detail, i) => (
                      <p
                        key={i}
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.85rem",
                          marginBottom: "0.2rem",
                        }}
                      >
                        {detail}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Support Hours
            <div
              style={{
                marginTop: "1.5rem",
                padding: "1rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                borderRadius: "16px",
                color: "white",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                <FaClock size={14} />
                <span style={{ fontWeight: "600" }}>Support Hours</span>
              </div>
              <p style={{ fontSize: "0.8rem", opacity: 0.9 }}>
                Monday - Friday: 9:00 AM - 6:00 PM
              </p>
              <p style={{ fontSize: "0.8rem", opacity: 0.9 }}>
                Saturday: 10:00 AM - 2:00 PM
              </p>
            </div> */}
          </motion.div>

          {/* Contact Form */}
          <motion.div
            variants={itemVariants}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              padding: isMobile ? "1.5rem" : "2rem",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.3rem" : "1.5rem",
                fontWeight: "bold",
                marginBottom: "1.5rem",
                color: getTextColor(),
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaComment color="#8b5cf6" /> Send Message
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaUser style={{ marginRight: "0.3rem", color: "#8b5cf6" }} />
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                  style={{
                    width: "100%",
                    padding: "0.8rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    outline: "none",
                    fontSize: "0.9rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaEnvelope
                    style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                  />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                  style={{
                    width: "100%",
                    padding: "0.8rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    outline: "none",
                    fontSize: "0.9rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              <div style={{ marginBottom: "1.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaRegEnvelope
                    style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                  />
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="What is this regarding?"
                  style={{
                    width: "100%",
                    padding: "0.8rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    outline: "none",
                    fontSize: "0.9rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  <FaComment
                    style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                  />
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Tell us how we can help you..."
                  style={{
                    width: "100%",
                    padding: "0.8rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    outline: "none",
                    fontSize: "0.9rem",
                    resize: "vertical",
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontFamily: "inherit",
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "1rem",
                  background: loading
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.3s",
                  boxShadow: loading
                    ? "none"
                    : "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                <FaPaperPlane /> {loading ? "Sending..." : "Send Message"}
              </motion.button>
            </form>
          </motion.div>
        </motion.div>

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{
            marginTop: "2rem",
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            Your information is protected with 256-bit encryption
          </span>
        </motion.div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Contact;

